import { useState } from 'react';
import Dialogo from '../../components/ui/Dialogo';
import { btnSecundario, btnPrimario, campo, money } from '../../components/ui/estilos';
import type { ModificadorSel } from '../../components/ModalModificadores';
import type { Hoja } from './useCarrito';
import { TIEMPOS } from './mesas';

const pastilla = (activa: boolean, fuerte = false): React.CSSProperties => ({
  height: 44, padding: '0 16px', borderRadius: 999, fontSize: 15, fontWeight: 700, cursor: 'pointer',
  background: activa ? (fuerte ? 'var(--strong)' : 'var(--brand)') : 'var(--surface)',
  color: activa ? (fuerte ? 'var(--on-strong)' : 'var(--on-brand)') : 'var(--ink)',
  border: activa ? 'none' : '1px solid var(--line-strong)',
});
const cuadro: React.CSSProperties = { ...btnSecundario, width: 52, height: 52, padding: 0, fontSize: 24, borderRadius: 12 };

export default function HojaPlatillo({ hoja, tiempoInicial, onConfirmar, onCancelar }: {
  hoja: Hoja; tiempoInicial: number;
  onConfirmar: (s: { mods: ModificadorSel[]; tiempo: number; notas: string; cantidad: number }) => void;
  onCancelar: () => void;
}) {
  const [sel, setSel] = useState<Record<string, string[]>>({});
  const [tiempo, setTiempo] = useState(tiempoInicial);
  const [notas, setNotas] = useState('');
  const [cantidad, setCantidad] = useState<number>(hoja.promo?.item.cantidad ?? 1);
  const [error, setError] = useState('');
  const enPromo = !!hoja.promo;

  const toggle = (g: any, id: string) => {
    setError('');
    setSel(prev => {
      const cur = prev[g.id] || [];
      if (g.tipo === 'unico') return { ...prev, [g.id]: cur.includes(id) ? [] : [id] };
      return { ...prev, [g.id]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  };

  const mods: ModificadorSel[] = hoja.grupos.flatMap(g => (sel[g.id] || []).map(id => {
    const o = g.opciones.find((x: any) => x.id === id);
    return {
      opcion_id: o.id, nombre: o.nombre, precio_extra: parseFloat(o.precio_extra || 0),
      insumo_id: o.insumo_id || null, operacion: o.operacion || null,
      cantidad_insumo: o.cantidad_insumo ? parseFloat(o.cantidad_insumo) : null,
    };
  }));
  const precio = (hoja.precio + mods.reduce((s, m) => s + m.precio_extra, 0)) * cantidad;

  const confirmar = () => {
    const falta = hoja.grupos.find(g => g.obligatorio && !(sel[g.id] || []).length);
    if (falta) { setError(`Elige una opción de "${falta.nombre}".`); return; }
    onConfirmar({ mods, tiempo, notas: notas.trim(), cantidad });
  };

  return (
    <Dialogo titulo={hoja.prod.nombre + (enPromo ? ` · ${hoja.promo!.promo.nombre}` : '')} onCerrar={onCancelar} ancho={560}>
      {hoja.grupos.map(g => (
        <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--muted)' }}>
            {g.nombre}{g.obligatorio ? ' · obligatorio' : g.tipo === 'unico' ? '' : ' · varios'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(g.opciones || []).map((o: any) => {
              const on = (sel[g.id] || []).includes(o.id);
              const extra = parseFloat(o.precio_extra || 0);
              return (
                <button key={o.id} onClick={() => toggle(g, o.id)} aria-pressed={on} style={pastilla(on)}>
                  {o.nombre}{extra > 0 ? ` (+${money(extra)})` : ''}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--muted)' }}>Tiempo</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(TIEMPOS).map(([t, label]) => (
            <button key={t} onClick={() => setTiempo(+t)} aria-pressed={tiempo === +t} style={pastilla(tiempo === +t, true)}>{label}</button>
          ))}
        </div>
      </div>

      <input value={notas} onChange={e => setNotas(e.target.value)} placeholder="Nota para cocina (opcional)" maxLength={200} style={campo} />

      {!enPromo && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => setCantidad(q => Math.max(1, q - 1))} aria-label="Menos" style={cuadro}>−</button>
          <div aria-live="polite" style={{ flex: 1, textAlign: 'center', fontSize: 26, fontWeight: 700 }}>{cantidad}</div>
          <button onClick={() => setCantidad(q => Math.min(99, q + 1))} aria-label="Más" style={cuadro}>+</button>
        </div>
      )}

      {error && <div role="alert" style={{ color: 'var(--danger)', fontWeight: 700 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onCancelar} style={{ ...btnSecundario, flex: 1, height: 56, fontSize: 17 }}>Cancelar</button>
        <button onClick={confirmar} style={{ ...btnPrimario, flex: 2, fontSize: 17 }}>Agregar · {money(precio)}</button>
      </div>
    </Dialogo>
  );
}
